/**
 * Thin fetch wrapper for the /api contract (packages/shared/src/api.ts).
 * - Session cookie (`credentials: 'include'`), CSRF header on every mutating request.
 * - JSON errors become a typed ApiError with the server's error code.
 * - 'unauthenticated' → global handler (redirect to /login).
 * - 'reauth_required' → global handler opens the TOTP re-auth dialog; on success the request is retried once.
 */
export type ApiErrorCode =
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'validation'
  | 'reauth_required'
  | 'totp_required'
  | 'gate_blocked'
  | 'read_only'
  | 'payment_required'
  | 'conflict'
  | 'network'
  | 'server_error'
  | (string & {});

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details: unknown;
  constructor(code: ApiErrorCode, message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export function isApiError(e: unknown, code?: ApiErrorCode): e is ApiError {
  return e instanceof ApiError && (code === undefined || e.code === code);
}

interface Handlers {
  onUnauthenticated: ((code: 'unauthenticated' | 'totp_required') => void) | null;
  onReauth: (() => Promise<boolean>) | null;
}
export const apiHandlers: Handlers = { onUnauthenticated: null, onReauth: null };

export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  form?: FormData;
  query?: Query;
  signal?: AbortSignal;
  /** Don't trigger the global login redirect (used by the session probe). */
  silentAuth?: boolean;
  /** Internal: whether a reauth retry is still allowed. */
  allowReauth?: boolean;
}

export const API_BASE = '/api';

export function apiUrl(path: string, query?: Query): string {
  const qs = new URLSearchParams();
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    }
  }
  const s = qs.toString();
  return `${API_BASE}${path}${s ? `?${s}` : ''}`;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? (opts.body !== undefined || opts.form ? 'POST' : 'GET');
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (method !== 'GET') headers['X-Requested-With'] = 'btw';
  let body: BodyInit | undefined;
  if (opts.form) body = opts.form;
  else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    res = await fetch(apiUrl(path, opts.query), { method, headers, body, credentials: 'include', signal: opts.signal });
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e;
    throw new ApiError('network', 'Network request failed', 0);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = undefined;
    }
  }

  if (res.ok) return data as T;

  const err = (data as { error?: { code?: string; message?: string; details?: unknown } } | undefined)?.error;
  const apiErr = new ApiError(err?.code ?? (res.status >= 500 ? 'server_error' : `http_${res.status}`), err?.message ?? res.statusText, res.status, err?.details);

  // 'totp_required' outside the login call means the session still needs its second factor (magic-link login).
  if ((apiErr.code === 'unauthenticated' || apiErr.code === 'totp_required') && !opts.silentAuth) apiHandlers.onUnauthenticated?.(apiErr.code === 'totp_required' ? 'totp_required' : 'unauthenticated');

  if (apiErr.code === 'reauth_required' && opts.allowReauth !== false && apiHandlers.onReauth) {
    const ok = await apiHandlers.onReauth();
    if (ok) return api<T>(path, { ...opts, allowReauth: false });
  }
  throw apiErr;
}

export const get = <T>(path: string, query?: Query, signal?: AbortSignal) => api<T>(path, { query, signal });
export const post = <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body: body ?? {} });
export const patch = <T>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body });
export const put = <T>(path: string, body: unknown) => api<T>(path, { method: 'PUT', body });
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' });
export const postForm = <T>(path: string, form: FormData) => api<T>(path, { method: 'POST', form });
